import { useQuery } from '@tanstack/react-query';
import { chamar } from '../../api';
import { ROTAS } from '@remoto/shared';
import { IconeArquivo, IconeBaixar } from '../icones';
import { ImagemDaConversa } from './ImagemDaConversa';
import { tamanhoLegivel } from '../../formato';
import { anexoNaMaquinaDesligada, FRASE_DA_MAQUINA_DESLIGADA } from './falta-do-anexo';

/**
 * O arquivo que a SESSÃO mandou para a conversa (`enviar_arquivo`).
 *
 * É a direção inversa do anexo do compositor, e nasceu de um pedido literal do
 * dono: ele mandava anexo e recebia de volta **o caminho** do arquivo, que
 * tinha de ir buscar na máquina. O caminho não é clicável de propósito — a
 * pasta de anexos fica fora da allowlist de raízes — então ele não abria nem
 * baixava nada; era texto.
 *
 * Imagem continua indo por `ImagemDaConversa`, que já existia: mesma
 * miniatura, mesmo Modal, mesma URL assinada. O que faltava era o resto — PDF,
 * planilha, `.zip` — e é isso que o cartão abaixo resolve.
 */

/** O bilhete de leitura vale 10 min no servidor; 8 evitam a corrida. */
const VALIDADE_MS = 8 * 60_000;

export interface AnexoEnviado {
  /** Nome do objeto no armazenamento (`<uuid>.<ext>`), que a rota remonta. */
  objeto: string;
  /** Nome original do arquivo no disco da máquina, medido lá. */
  nome: string;
  /** Bytes, medidos no disco — nunca o que a chamada declarou. */
  tamanho: number;
  mime: string;
}

export function AnexoDaSessao({
  sessaoId,
  anexo,
}: {
  sessaoId: string;
  anexo: AnexoEnviado;
}): React.JSX.Element {
  /*
    Imagem tem componente próprio desde antes disto, e ele faz mais do que um
    cartão faria: miniatura com teto de altura, Modal em 75vh, pedido de
    bilhete novo quando o antigo vence com a aba aberta. Duplicar aqui seria
    criar a segunda cópia que diverge — a mesma armadilha do `dentroDaRaiz`,
    que existia em três lugares e errava num deles.
  */
  if (anexo.mime.startsWith('image/')) {
    return <ImagemDaConversa sessaoId={sessaoId} nome={anexo.objeto} legenda={anexo.nome} />;
  }
  return <CartaoDeArquivo sessaoId={sessaoId} anexo={anexo} />;
}

function CartaoDeArquivo({
  sessaoId,
  anexo,
}: {
  sessaoId: string;
  anexo: AnexoEnviado;
}): React.JSX.Element {
  const { data, isError, isPending, error } = useQuery({
    queryKey: ['anexo-url', sessaoId, anexo.objeto],
    queryFn: () =>
      chamar<{ url: string }>(
        `${ROTAS.sessoes}/${sessaoId}/anexos/${encodeURIComponent(anexo.objeto)}/url`,
      ),
    staleTime: VALIDADE_MS,
    gcTime: VALIDADE_MS,
    // 404 é estado terminal: o objeto foi podado, e insistir só atrasa a
    // mensagem honesta.
    retry: false,
  });

  return (
    <div className="mt-1 flex items-center gap-3 rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2">
      <span className="shrink-0 text-zinc-500">
        <IconeArquivo />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-zinc-200" title={anexo.nome}>
          {anexo.nome}
        </span>
        <span className="block text-xs text-zinc-500">
          {tamanhoLegivel(anexo.tamanho)}
          {isError
            ? anexoNaMaquinaDesligada(error)
              ? ` · ${FRASE_DA_MAQUINA_DESLIGADA}`
              : ' · não está mais guardado'
            : ''}
        </span>
      </span>
      {/*
        O botão só aparece quando há endereço. Um "Baixar" que não baixa é pior
        que nenhum botão: leva a pessoa a clicar de novo achando que o clique
        não pegou — foi assim que o "Encerrar" da lista virou "o botão não
        funciona".
      */}
      {data?.url ? (
        <a
          href={data.url}
          target="_blank"
          rel="noreferrer"
          download={anexo.nome}
          className="shrink-0 rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-800"
        >
          <span className="flex items-center gap-1">
            <IconeBaixar /> Baixar
          </span>
        </a>
      ) : (
        <span className="shrink-0 text-xs text-zinc-600">{isPending ? '…' : ''}</span>
      )}
    </div>
  );
}
